import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { cj } from 'src/utils/classNames';
import Button from '../primitives/Button/Button';
import useModal from './hooks/useModal';
import ResponsiveModal from './ResponsiveModal/ResponsiveModal';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';
import { useEffectiveMarginMode } from 'src/hooks/marginMode/useEffectiveMarginMode';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import useIsIsolatedMarginEnabledForMarket from 'src/hooks/marginMode/useIsIsolatedMarginEnabledForMarket';

const CROSS_COLLATERAL_SYMBOLS = ['ETH', 'BTC', 'USDC', 'SOL'];
const ISOLATED_COLLATERAL_SYMBOLS = ['USDC'];
const ISOLATED_AVAILABLE_TOOLTIP_STORAGE_KEY =
	'isolated-available-tooltip-status';

export default function PerpMarginModeModal() {
	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);
	const { hide, push } = useModal();
	const isWalletConnected = useWalletIsConnected();
	const currentUserAccount = useDriftAccountStore((s) =>
		s.getCurrentUserAccount()
	);
	const userKey = currentUserAccount?.userKey;

	// Get market ID key for store key
	const marketIdKey = selectedMarket?.marketId?.key;

	// Get margin mode settings from store
	const { getMarginModeForMarket, setMarginModeForMarket } =
		useMarketSettingsStore();

	const { mode: currentMarginMode, isLocked } =
		useEffectiveMarginMode(marketIdKey);

	const isIsolatedMarginEnabledForMarket = useIsIsolatedMarginEnabledForMarket({
		marketSymbol: selectedMarket?.uiSymbols.marketDisplaySymbol,
	});

	// Determine if user can change margin mode (no open position)
	const canChangeMarginMode = useMemo(() => !isLocked, [isLocked]);

	// Determine current margin mode from position, store, or default to cross
	const effectiveMarginMode = useMemo(() => {
		// allow callers that still use local name
		const mode =
			currentMarginMode ??
			getMarginModeForMarket(marketIdKey, userKey ?? '') ??
			'cross';
		// If isolated is not enabled for this market, default to cross
		return mode === 'isolated' && !isIsolatedMarginEnabledForMarket
			? 'cross'
			: mode;
	}, [
		currentMarginMode,
		marketIdKey,
		getMarginModeForMarket,
		isIsolatedMarginEnabledForMarket,
		userKey,
	]);

	const [selection, setSelection] = useState<'cross' | 'isolated'>(
		effectiveMarginMode
	);
	const [isolatedTooltipStatus, setIsolatedTooltipStatus] = useSyncLocalStorage<
		'pending' | 'seen'
	>(ISOLATED_AVAILABLE_TOOLTIP_STORAGE_KEY);

	// Sync selection when effective margin mode changes (e.g., when flag loads)
	useEffect(() => {
		setSelection(effectiveMarginMode);
	}, [effectiveMarginMode]);

	// Handler to update local selection when user can change margin mode
	const handleSelectionChange = useCallback(
		(newSelection: 'cross' | 'isolated') => {
			// Prevent selecting isolated if not enabled for this market
			if (newSelection === 'isolated' && !isIsolatedMarginEnabledForMarket) {
				return;
			}
			if (canChangeMarginMode) {
				setSelection(newSelection);
			}
		},
		[canChangeMarginMode, isIsolatedMarginEnabledForMarket]
	);

	const title = selectedMarket?.uiSymbols.marketDisplaySymbol
		? `Margin mode for ${selectedMarket.uiSymbols.marketDisplaySymbol}`
		: 'Margin Mode';

	return (
		<ResponsiveModal.Container onClose={hide} dataTestId="margin-mode-modal">
			<ResponsiveModal.Title closeButtonTestId="margin-mode-modal-close">
				{title}
			</ResponsiveModal.Title>

			<ResponsiveModal.Body>
				<MarginModeCardButton
					title="Cross Margin"
					description="All positions and orders under this mode share the same margin pool. Liquidation risk applies to your entire balance and all open positions. Enable trading perps with multi-asset as collateral."
					isSelected={selection === 'cross'}
					disabled={selection !== 'cross' && !canChangeMarginMode}
					collateralSymbols={CROSS_COLLATERAL_SYMBOLS}
					onClick={() => handleSelectionChange('cross')}
				/>

				<MarginModeCardButton
					title="Isolated Margin"
					comingSoon={!isIsolatedMarginEnabledForMarket}
					description={
						'Each position has its own margin allocation. Liquidation risk is limited to the specific position, not your full account balance. Margin can be increased or reduced on existing positions.'
					}
					isSelected={selection === 'isolated'}
					disabled={
						!isIsolatedMarginEnabledForMarket ||
						(selection !== 'isolated' && !canChangeMarginMode)
					}
					collateralSymbols={ISOLATED_COLLATERAL_SYMBOLS}
					onClick={() => handleSelectionChange('isolated')}
				/>

				<p>Margin mode changes apply only to the market you have selected.</p>

				<p className="text-star-yellow">
					Margin mode cannot be changed while you have an open position or
					order.
				</p>
			</ResponsiveModal.Body>

			<ResponsiveModal.Footer>
				{({ close }) => (
					<Button
						size="lg"
						className="w-full"
						onClick={() => {
							if (!isWalletConnected) {
								push({ type: 'connect_wallet', props: {} });
								return;
							}
							if (!canChangeMarginMode || !marketIdKey) {
								close();
								return;
							}
							if (
								selection === 'isolated' &&
								isolatedTooltipStatus !== 'seen'
							) {
								setIsolatedTooltipStatus('pending');
							}
							setMarginModeForMarket(marketIdKey, selection, userKey ?? '');
							close();
						}}
						dataTestId="margin-mode-modal-submit"
					>
						{isWalletConnected ? 'Confirm' : 'Connect Wallet'}
					</Button>
				)}
			</ResponsiveModal.Footer>
		</ResponsiveModal.Container>
	);
}

interface MarginModeCardButtonProps {
	title: string;
	description: string;
	isSelected: boolean;
	collateralSymbols: string[];
	comingSoon?: boolean;
	disabled?: boolean;
	onClick: () => void;
}

function MarginModeCardButton({
	title,
	description,
	isSelected,
	collateralSymbols,
	comingSoon,
	disabled,
	onClick,
}: MarginModeCardButtonProps) {
	const isUsdcCollateralOnly =
		collateralSymbols.length === 1 && collateralSymbols[0] === 'USDC';

	return (
		<button
			className={cj(
				'group flex flex-col gap-y-lg rounded-sm border p-lg text-left',
				isSelected
					? 'border-button-primary-bg bg-button-selector'
					: 'border-input-border bg-input-bg not-disabled:hover:border-purple-link',
				comingSoon && 'h-33'
			)}
			disabled={disabled || comingSoon}
			onClick={onClick}
		>
			<div className="flex w-full items-center justify-between">
				<span className="typo-label-md text-text-default group-disabled:text-text-disabled">
					{title}
				</span>

				<div
					className={cj(
						'flex items-center gap-x-xs typo-body-2xs transition-colors',
						isSelected
							? 'text-text-default'
							: 'text-text-secondary group-disabled:text-text-disabled'
					)}
				>
					<span>Collateral:</span>

					<div className="flex items-center -space-x-xs">
						{collateralSymbols.map((symbol) => (
							<MarketIcon
								key={symbol}
								marketSymbol={symbol}
								customHeight={16}
								customWidth={16}
							/>
						))}

						{!isUsdcCollateralOnly && (
							<div className="grid size-[17px] place-items-center rounded-full bg-main-bg text-[7px] font-bold text-text-default">
								40+
							</div>
						)}
					</div>

					{isUsdcCollateralOnly && <span>USDC only</span>}
				</div>
			</div>

			{comingSoon ? (
				<div className="m-auto rounded-md bg-button-disabled px-md py-sm">
					Coming soon for this market
				</div>
			) : (
				<p
					className={cj(
						'transition-colors',
						isSelected
							? 'text-text-default'
							: 'text-text-secondary group-disabled:text-text-disabled'
					)}
				>
					{description}
				</p>
			)}
		</button>
	);
}
